import { memo, useMemo, useCallback, useRef } from 'react';
import { ServiceCard } from './ServiceCard';

/**
 * VirtualizedServiceList Component
 * Renders large lists of services with virtualization for performance
 */
export const VirtualizedServiceList = memo(({ 
  services,
  onBookNow,
  onLearnMore,
  onToggleFavorite,
  favorites = new Set(),
  layout = 'grid', // 'grid' or 'list'
  itemHeight = 200,
  containerHeight = 600,
  overscan = 5,
  loading = false,
  emptyMessage = "No services found",
  className = ''
}) => {
  const containerRef = useRef(null);

  // Calculate visible range
  const visibleRange = useMemo(() => {
    if (!services.length) return { start: 0, end: 0 };
    
    const visibleCount = Math.ceil(containerHeight / itemHeight);
    const totalWithOverscan = visibleCount + (overscan * 2);
    
    return {
      start: 0,
      end: Math.min(totalWithOverscan, services.length)
    };
  }, [services.length, containerHeight, itemHeight, overscan]);

  // Get visible services
  const visibleServices = useMemo(() => {
    return services.slice(visibleRange.start, visibleRange.end);
  }, [services, visibleRange.start, visibleRange.end]);

  // Grid specific calculations
  const gridConfig = useMemo(() => {
    if (layout !== 'grid') return null;
    
    const itemWidth = 320; // Approximate card width
    const gap = 16; // Grid gap
    const containerWidth = containerRef.current?.offsetWidth || 1200;
    const columns = Math.floor((containerWidth + gap) / (itemWidth + gap));
    const rows = Math.ceil(services.length / columns);
    
    return {
      columns,
      rows,
      itemWidth,
      itemHeight,
      gap,
      totalHeight: rows * (itemHeight + gap) - gap
    };
  }, [layout, services.length, itemHeight]);

  const handleServiceAction = useCallback((action, service, serviceId) => {
    switch (action) {
      case 'book':
        onBookNow?.(service);
        break;
      case 'learn':
        onLearnMore?.(service);
        break;
      case 'favorite':
        onToggleFavorite?.(serviceId);
        break;
      default:
        break;
    }
  }, [onBookNow, onLearnMore, onToggleFavorite]);

  // Loading state
  if (loading) {
    return (
      <div className={`virtualized-service-list ${className}`}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
          <span className="ml-3 text-gray-600">Loading services...</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (!services.length) {
    return (
      <div className={`virtualized-service-list ${className}`}>
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🕉️</div>
          <p className="text-gray-500 text-lg">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Grid layout
  if (layout === 'grid') {
    return (
      <div 
        ref={containerRef}
        className={`virtualized-service-list virtualized-service-list--grid ${className}`}
        style={{ height: containerHeight }}
      >
        <div 
          className="grid gap-4 auto-rows-max"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(300px, 1fr))`,
            height: gridConfig?.totalHeight || 'auto'
          }}
        >
          {visibleServices.map((service, index) => (
            <div 
              key={service.id}
              className="virtualized-service-list__item"
              style={{
                gridRowStart: Math.floor((visibleRange.start + index) / (gridConfig?.columns || 1)) + 1
              }}
            >
              <ServiceCard
                service={service}
                onBookNow={(svc) => handleServiceAction('book', svc)}
                onLearnMore={(svc) => handleServiceAction('learn', svc)}
                onToggleFavorite={(id) => handleServiceAction('favorite', null, id)}
                isFavorite={favorites.has(service.id)}
                layout={layout}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // List layout
  return (
    <div 
      ref={containerRef}
      className={`virtualized-service-list virtualized-service-list--list ${className}`}
      style={{ height: containerHeight, overflow: 'auto' }}
    >
      <div 
        style={{ height: services.length * itemHeight }}
        className="relative"
      >
        {visibleServices.map((service, index) => (
          <div
            key={service.id}
            className="virtualized-service-list__item absolute w-full"
            style={{
              top: (visibleRange.start + index) * itemHeight,
              height: itemHeight
            }}
          >
            <ServiceCard
              service={service}
              onBookNow={(svc) => handleServiceAction('book', svc)}
              onLearnMore={(svc) => handleServiceAction('learn', svc)}
              onToggleFavorite={(id) => handleServiceAction('favorite', null, id)}
              isFavorite={favorites.has(service.id)}
              layout={layout}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

VirtualizedServiceList.displayName = 'VirtualizedServiceList';

export default VirtualizedServiceList;
